import { ThemeEvents, VariantUpdateEvent } from "@theme/events";
import { shopifyFormatMoney } from "@theme/utilities";

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
    connectedCallback() {
        const closestSection = this.closest(
            ".shopify-section, dialog, .js-product",
        );
        this.showSupercents = this.dataset?.showSupercents === "true";
        this.discountCodePrice = this.querySelector(
            "[data-discount-code-price]",
        );
        this.discountRatio =
            this.discountCodePrice?.dataset?.discountRatio * 1 || 0;
        this.discountCodeJsValue =
            this.discountCodePrice &&
            this.discountCodePrice.querySelector(".js-value");
        if (!closestSection) return;
        closestSection.addEventListener(
            ThemeEvents.variantUpdate,
            this.updatePrice,
        );
    }

    disconnectedCallback() {
        const closestSection = this.closest(
            ".shopify-section, dialog, .js-product",
        );
        if (!closestSection) return;
        closestSection.removeEventListener(
            ThemeEvents.variantUpdate,
            this.updatePrice,
        );
    }

    /**
     * Updates the price.
     * @param {VariantUpdateEvent} event - The variant update event.
     */
    updatePrice = (event) => {
        if (event.detail.data.newProduct) {
            this.dataset.productId = event.detail.data.newProduct.id;
        } else if (
            event.target instanceof HTMLElement &&
            event.target.dataset.productId !== this.dataset.productId
        ) {
            return;
        }

        const variant = event.detail.data?.variant;

        if (variant) {
            const supText = this.showSupercents
                ? '<sup aria-hidden="true">00</sup>'
                : "";
            const currentPriceSpan = this.querySelector(
                ".price__current, .price__original",
            );
            const currentPrice = this.querySelector(
                ".price__current .js-value, .price__original .js-value",
            );
            const priceWasEl = this.querySelector(".price__was");
            const compareAtPriceEl = this.querySelector(
                ".price__was .js-value",
            );
            const currentCapInfo = this.querySelector(
                '[ref="priceContainer"] .compare-at-price',
            );
            const compareAtPrice = variant?.compare_at_price;
            if (currentPrice)
                currentPrice.innerHTML =
                    this.discountRatio > 0 && !isNaN(this.discountRatio)
                        ? `<s>${shopifyFormatMoney(variant?.price) + supText}</s>`
                        : shopifyFormatMoney(variant?.price) + supText;
            currentPriceSpan.querySelector(".js-label").innerHTML =
                this.discountRatio > 0
                    ? `Original price: ${shopifyFormatMoney(variant?.price)}`
                    : `Sale price: ${shopifyFormatMoney(variant?.price)}`;
            /*  currentPriceSpan?.setAttribute(
                 "aria-label",
                 `${this.discountRatio > 0 ? 'Original price' : 'Sale price'}: ${shopifyFormatMoney(variant?.price)}`,
             ); */

            if (compareAtPrice > variant?.price) {
                if (compareAtPriceEl && compareAtPrice) {
                    compareAtPriceEl.innerHTML =
                        shopifyFormatMoney(compareAtPrice) + supText;
                    /* priceWasEl.setAttribute(
                        "aria-label",
                        `Regular price: ${shopifyFormatMoney(compareAtPrice)}`,
                    ); */
                    priceWasEl.querySelector(".js-label").innerHTML =
                        "Regular price:" +
                        " " +
                        shopifyFormatMoney(compareAtPrice);
                }
                if (currentCapInfo) currentCapInfo.removeAttribute("hidden");
                if (priceWasEl) priceWasEl.hidden = false;
            } else {
                if (priceWasEl) priceWasEl.hidden = true;
                if (currentCapInfo) currentCapInfo.setAttribute("hidden", "");
            }
            if (
                this.discountRatio > 0 &&
                !isNaN(this.discountRatio) &&
                this.discountCodeJsValue
            ) {
                this.discountCodeJsValue.innerHTML = `${shopifyFormatMoney(variant?.price * (1 - this.discountRatio), null, false)}`;
                this.discountCodePrice.querySelector(".js-label").innerHTML =
                    "Discounted price:" +
                    " " +
                    shopifyFormatMoney(
                        variant?.price * (1 - this.discountRatio),
                        null,
                        false,
                    );
                /* this.discountCodePrice?.setAttribute(
                    "aria-label",
                    `Discounted price: ${shopifyFormatMoney(variant?.price * (1 - this.discountRatio), null, false)}`,
                ); */
            }
        } else {
            const newPrice = event.detail.data.html.querySelector(
                "product-price .price__current",
            );
            const newCompareAtPrice = event.detail.data.html.querySelector(
                "product-price .price__was",
            );
            const currentPrice = this.querySelector(".price__current");
            const currentCompareAtPrice = this.querySelector(".price__was");
            const currentCapInfo = this.querySelector(
                '[ref="priceContainer"] .compare-at-price',
            );
            const compareAtPriceInfo = event.detail.data?.html?.querySelector(
                "product-price .compare-at-price",
            );

            if (!newPrice || !currentPrice) return;

            if (currentPrice.innerHTML !== newPrice.innerHTML) {
                currentPrice.replaceWith(newPrice);
                currentCompareAtPrice.replaceWith(newCompareAtPrice);
            }
            if (compareAtPriceInfo && !currentCapInfo) {
                const wrapper = this.querySelector('[ref="priceContainer"]');
                if (wrapper) wrapper.appendChild(compareAtPriceInfo);
            }
        }
    };
}

if (!customElements.get("product-price")) {
    customElements.define("product-price", ProductPrice);
}
